import { supabase } from '@/lib/supabase/client';

/**
 * Debug the materialized view to understand why Georgia data is missing
 */

export async function debugMaterializedView() {
  console.log('🔍 Debugging materialized view data...');

  try {
    // 1. Check total count in materialized view
    console.log('📊 Step 1: Checking total records in materialized view...');

    const { count: totalCount, error: countError } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select('*', { count: 'exact', head: true });

    if (countError) {
      console.error('❌ Error counting materialized view:', countError);
    } else {
      console.log(`✅ Total records in cars_with_safety_recalls_mv: ${totalCount}`);
    }

    // 2. Check what states/regions are available
    console.log('\n📍 Step 2: Checking available states/regions...');

    const { data: regions, error: regionsError } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select('seller_region')
      .not('seller_region', 'is', null)
      .limit(1000);

    if (regionsError) {
      console.error('❌ Error getting regions:', regionsError);
    } else if (regions) {
      const uniqueRegions = [...new Set(regions.map(r => r.seller_region))];
      console.log(`📍 Found ${uniqueRegions.length} unique regions:`, uniqueRegions.sort());

      // Check if Georgia variations exist
      const georgiaVariations = uniqueRegions.filter(
        region =>
          region &&
          (region.toLowerCase().includes('ga') ||
            region.toLowerCase().includes('georgia') ||
            region.toLowerCase().includes('atl'))
      );
      console.log('🍑 Georgia-related regions found:', georgiaVariations);
    }

    // 3. Check sample data to see what it looks like
    console.log('\n📋 Step 3: Checking sample data structure...');

    const { data: sampleData, error: sampleError } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select(
        'vin, service_provider_name, seller_city, seller_region, seller_latitude, seller_longitude, safety_recalls'
      )
      .not('seller_latitude', 'is', null)
      .not('seller_longitude', 'is', null)
      .limit(10);

    if (sampleError) {
      console.error('❌ Error getting sample data:', sampleError);
    } else if (sampleData && sampleData.length > 0) {
      console.log(`📋 Sample data (${sampleData.length} records):`);
      sampleData.slice(0, 3).forEach((record, i) => {
        console.log(
          `  ${i + 1}. ${record.service_provider_name} - ${record.seller_city}, ${record.seller_region}`
        );
        console.log(`     Lat: ${record.seller_latitude}, Lng: ${record.seller_longitude}`);
        console.log(`     VIN: ${record.vin}`);
        if (record.safety_recalls) {
          try {
            const recalls =
              typeof record.safety_recalls === 'string'
                ? JSON.parse(record.safety_recalls)
                : record.safety_recalls;
            console.log(`     Recalls: ${recalls?.length || 0}`);
          } catch (e) {
            console.log(`     Recalls: Invalid JSON - ${e.message}`);
          }
        }
      });
    } else {
      console.log('📋 No sample data found - materialized view appears to be empty');
    }

    // 4. Check if there's any data with coordinates in Southeast US
    console.log('\n🌎 Step 4: Checking Southeast US coordinates...');

    const { data: southeastData, error: southeastError } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select(
        'service_provider_name, seller_city, seller_region, seller_latitude, seller_longitude'
      )
      .gte('seller_latitude', 25) // Florida south
      .lte('seller_latitude', 40) // Virginia north
      .gte('seller_longitude', -90) // Louisiana west
      .lte('seller_longitude', -75) // Carolinas east
      .not('seller_latitude', 'is', null)
      .not('seller_longitude', 'is', null)
      .limit(20);

    if (southeastError) {
      console.error('❌ Error getting Southeast data:', southeastError);
    } else {
      console.log(`🌎 Found ${southeastData?.length || 0} vehicles in Southeast US bounds`);
      if (southeastData && southeastData.length > 0) {
        const statesInSoutheast = [...new Set(southeastData.map(d => d.seller_region))];
        console.log('🌎 States in Southeast data:', statesInSoutheast.sort());
      }
    }

    // 5. Check materialized view refresh status (if possible)
    console.log('\n⏰ Step 5: Checking if we can determine view freshness...');

    // Try to get some metadata about when data might have been inserted
    const { data: recentData, error: recentError } = await supabase
      .from('cars_with_safety_recalls_mv')
      .select('vin, service_provider_name')
      .order('vin', { ascending: false })
      .limit(5);

    if (recentError) {
      console.error('❌ Error getting recent data:', recentError);
    } else {
      console.log(
        `⏰ Most recent VINs in materialized view: ${recentData?.map(d => d.vin).join(', ')}`
      );
    }

    return {
      totalRecords: totalCount || 0,
      hasSampleData: (sampleData?.length || 0) > 0,
      southeastRecords: southeastData?.length || 0,
      isEmpty: (totalCount || 0) === 0
    };
  } catch (error) {
    console.error('❌ Debug failed:', error);
    return null;
  }
}

// Export to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugMaterializedView = debugMaterializedView;
}

export default debugMaterializedView;
